import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { t } from "i18next";
import { FaLocationDot, FaMapPin, FaLocationCrosshairs } from "react-icons/fa6";
import { IoCloseSharp } from "react-icons/io5";
import { Input } from "./ui/input";
import Proximiio from "proximiio-js-library";
import { cn, getFilteredItems, getSortedPOIs } from "@/lib/utils";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import useMapStore from "@/store/mapStore";
import useNominatimGeocoding, {
  NominatimResult,
} from "@/hooks/useNominatimGeocoding";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import useDebounce from "@/hooks/useDebounce";
import { Button } from "./ui/button";
import { SortedPoiItemModel } from "@/models/sortedPoiItem.model";
import useFeaturesStore from "@/store/featuresStore";
import Feature from "proximiio-js-library/lib/models/feature";

function CityRouteForm() {
  const features = useFeaturesStore((state) => state.features);
  const floors = useMapStore((state) => state.floors);
  const cityStart = useMapStore((state) => state.cityStart);
  const cityFinish = useMapStore((state) => state.cityFinish);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const navMode = useMapStore((state) => state.navMode);
  const kioskMode = useMapStore((state) => state.kioskMode);
  const currentLang = useMapStore((state) => state.currentLang);
  const appSession = useMapStore((state) => state.appSession);

  const [startSearch, setStartSearch] = useState("");
  const [finishSearch, setFinishSearch] = useState("");
  const [query, setQuery] = useState<string>("");
  const [activeMenu, setActiveMenu] = useState<"start" | "finish" | undefined>(
    navMode === "city-from" ? "finish" : "start",
  );
  const [error, setError] = useState<string>("");
  const [foundInTitle, setFoundInTitle] = useState<SortedPoiItemModel[]>([]);
  const [foundInDescription, setFoundInDescription] = useState<
    SortedPoiItemModel[]
  >([]);

  const setCityStart = useMapStore((state) => state.setCityStart);
  const setCityFinish = useMapStore((state) => state.setCityFinish);
  const cancelRouting = useMapStore((state) => state.cancelRouting);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);

  const debouncedQuery = useDebounce(query, 500);
  const { results, loading } = useNominatimGeocoding(
    debouncedQuery,
    currentLang,
  );

  const combiNavEnabled =
    import.meta.env.VITE_WAYFINDING_ENABLE_COMBINAV === "true";
  const startRef = useRef<HTMLInputElement>(null);
  const destinationRef = useRef<HTMLInputElement>(null);
  const url = useMemo(() => new URL(window.location.href), []);
  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );

  useEffect(() => {
    const mallTitle = t(import.meta.env.VITE_WAYFINDING_MALL_TITLE);
    const entryLongitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LONGITUDE;
    const entryLatitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LATITUDE;

    const mallFeature = {
      title: mallTitle,
      coordinates: [+entryLongitude, +entryLatitude],
    };

    if (navMode === "city-from") {
      setActiveMenu("finish");
      setCityFinish({} as { title: string; coordinates: number[] });

      if (combiNavEnabled) {
        urlParams.set("navMode", "city-from");
        url.search = urlParams.toString();
        window.history.replaceState({}, "", url);
      } else {
        setCityStart(mallFeature);
      }
    }
    if (navMode === "city-to") {
      setActiveMenu("start");
      setCityStart({} as { title: string; coordinates: number[] });

      if (combiNavEnabled) {
        urlParams.set("navMode", "city-to");
        url.search = urlParams.toString();
        window.history.replaceState({}, "", url);
      } else {
        setCityFinish(mallFeature);
      }
    }
  }, [navMode, setCityStart, setCityFinish, combiNavEnabled, urlParams, url]);

  useEffect(() => {
    const search = activeMenu === "start" ? startSearch : finishSearch;
    const pois = getSortedPOIs({ features, currentLang, floors });

    const filtered = getFilteredItems({
      items: pois,
      search,
      language: currentLang,
    }) as SortedPoiItemModel[];
    const foundInTitle = filtered.filter((item) => !item.foundInDescription);
    const foundInDescription = filtered.filter(
      (item) => item.foundInDescription,
    );

    setFoundInTitle(foundInTitle);
    setFoundInDescription(foundInDescription);
  }, [startSearch, finishSearch, activeMenu, currentLang, features, floors]);

  const inputChangeHandler = (input: "start" | "finish", value: string) => {
    if (input === "start" && cityStart) {
      setCityStart({} as { title: string; coordinates: number[] });
      setStartSearch(value);
    }
    if (input === "finish" && cityFinish) {
      setCityFinish({} as { title: string; coordinates: number[] });
      setFinishSearch(value);
    }
    if (
      (navMode === "city-to" && input === "start") ||
      (navMode === "city-from" && input === "finish")
    ) {
      setQuery(value);
    }
  };

  const inputFocusHandler = (input: "start" | "finish") => {
    setActiveMenu(input);
    if (input === "start" && cityStart?.title) {
      setStartSearch(cityStart?.title);
    }
    if (input === "finish" && cityFinish?.title) {
      setFinishSearch(cityFinish?.title);
    }
  };

  const inputBlurHandler = () => {
    setTimeout(() => {
      if (!cityStart?.title) {
        // resetView();
      }
      // setActiveMenu(undefined);
    }, 500);
  };

  const onSelectHandler = useCallback(
    (result: NominatimResult | SortedPoiItemModel | Feature) => {
      const isPoi = typeof result === "object" && "id" in result;

      if (navMode === "city-from") {
        if (isPoi) {
          setCityStart({
            title: result.properties?.title ? result.properties?.title : "",
            coordinates: result.geometry.coordinates,
            poiId: result.id,
          });
        } else {
          setCityFinish({
            title: result.name ? result.name : result.display_name,
            coordinates: [+result.lon, +result.lat],
          });
        }
        if (combiNavEnabled) {
          setQuery("");
          startRef.current?.focus();
        }
      }

      if (navMode === "city-to") {
        if (isPoi) {
          setCityFinish({
            title: result.properties?.title ? result.properties?.title : "",
            coordinates: result.geometry.coordinates,
            poiId: result.id,
          });
          const finishFeature = features.find((f) => f.id === result.id);

          if (combiNavEnabled) {
            urlParams.set("remote_id", result.id);
            url.search = urlParams.toString();
            window.history.replaceState({}, "", url);
          }

          if (finishFeature && cityStart.title) {
            setRouteFinish(finishFeature);
          }
        } else {
          setCityStart({
            title: result.name ? result.name : result.display_name,
            coordinates: [+result.lon, +result.lat],
          });
          if (cityFinish.poiId) {
            const finishFeature = features.find(
              (f) => f.id === cityFinish.poiId,
            );
            if (finishFeature && result.display_name) {
              setRouteFinish(finishFeature);
            }
          }
        }
        if (combiNavEnabled) {
          setQuery("");
          destinationRef.current?.focus();
        }
      }
    },
    [
      cityStart.title,
      combiNavEnabled,
      features,
      navMode,
      cityFinish.poiId,
      setCityFinish,
      setCityStart,
      setRouteFinish,
      urlParams,
      url,
    ],
  );

  useEffect(() => {
    const destinationParam = urlParams.get("remote_id");

    const destinationFeature = destinationParam
      ? features.find(
          (f) =>
            f.id?.toLowerCase() === destinationParam?.toLowerCase() ||
            f.properties.id?.toLowerCase() ===
              destinationParam?.toLowerCase() ||
            f.properties.title?.toLowerCase() ===
              destinationParam?.toLowerCase() ||
            f.properties.remote_id?.toString().toLowerCase() ===
              destinationParam?.toLowerCase(),
        )
      : undefined;

    if (destinationParam && destinationFeature) {
      onSelectHandler(destinationFeature);
      startRef.current?.focus();
    }
  }, []);

  const onCloseHandle = () => {
    if (combiNavEnabled) {
      urlParams.delete("remote_id");
      urlParams.delete("navMode");
      url.search = urlParams.toString();
      window.history.replaceState({}, "", url);
    }
    console.log("close handler");
    cancelRouting();
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (navMode === "city-from") {
            setCityFinish({
              title: t("current-position"),
              coordinates: [
                +position.coords.longitude,
                +position.coords.latitude,
              ],
            });
          }
          if (navMode === "city-to") {
            setCityStart({
              title: t("current-position"),
              coordinates: [
                +position.coords.longitude,
                +position.coords.latitude,
              ],
            });
          }
          setError(""); // Clear any previous errors
        },
        (err) => {
          setError("Location access denied or unavailable.");
          console.log(err);
        },
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    if (cityStart?.title && cityFinish?.title) {
      const userData = {
        osName,
        browserName,
        mobileModel,
        mobileVendor,
        deviceType,
      };

      new Proximiio.InteractionLogger({
        interactionType: "city-nav",
        targetElementTitle: cityFinish.title,
        metadata: {
          ...userData,
          cityStartTitle: cityStart?.title,
          cityFinishTitle: cityFinish?.title,
          cityStartCoordinates: JSON.stringify(cityStart?.coordinates),
          cityFinishCoordinates: JSON.stringify(cityFinish?.coordinates),
        },
        language: currentLang,
        session: appSession,
      });
    }
  }, [cityStart, cityFinish, appSession, currentLang]);

  return (
    <>
      {mobileMode && (
        <div className="absolute z-50 h-full w-full bg-background/80 backdrop-blur-sm"></div>
      )}
      <motion.div
        key="list"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0 }}
        transition={{ duration: 0.2 }}
        className={cn(
          "absolute left-3 right-3 top-3 z-100 flex max-h-96 min-h-[384px] flex-col rounded-xl p-5 lg:left-10 lg:top-10 kiosk:top-1/3",
          activeMenu && "bg-white shadow-[0px_1px_6px_3px_rgba(0,0,0,0.5)]",
        )}
      >
        <div
          className="flex flex-col items-center gap-3"
          style={{
            //@ts-expect-error color
            "--custom-handle-bg": `var(--primary)`,
          }}
        >
          <div className="relative flex w-full items-center">
            <FaLocationDot className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
            <IoCloseSharp
              className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
              onClick={() => onCloseHandle()}
            />
            <Input
              type="text"
              placeholder={
                navMode === "city-from"
                  ? t("choose-start")
                  : t("type-your-address")
              }
              className="rounded-full border-2 border-primary px-10 text-base"
              disabled={!combiNavEnabled && activeMenu === "finish"}
              autoFocus={activeMenu === "start"}
              value={cityStart?.title ? cityStart.title : startSearch}
              onChange={(e) => inputChangeHandler("start", e.target.value)}
              onFocus={() => inputFocusHandler("start")}
              onBlur={inputBlurHandler}
              ref={startRef}
            />
          </div>

          <div className="relative flex w-full items-center">
            <FaMapPin className="absolute text-primary group-data-[dir=ltr]:left-4 group-data-[dir=rtl]:right-4" />
            <IoCloseSharp
              className="absolute cursor-pointer text-2xl text-primary hover:text-rose-600 group-data-[dir=ltr]:right-4 group-data-[dir=rtl]:left-4"
              onClick={() => onCloseHandle()}
            />
            <Input
              type="text"
              placeholder={
                navMode === "city-to"
                  ? t("destination")
                  : t("type-your-address")
              }
              className="rounded-full border-2 border-primary px-10 text-base"
              disabled={!combiNavEnabled && activeMenu === "start"}
              autoFocus={activeMenu === "finish"}
              value={cityFinish?.title ? cityFinish.title : finishSearch}
              onChange={(e) => inputChangeHandler("finish", e.target.value)}
              onFocus={() => inputFocusHandler("finish")}
              onBlur={inputBlurHandler}
              ref={destinationRef}
            />
          </div>

          <div className="w-full">
            {!kioskMode &&
              ((navMode === "city-to" && activeMenu === "start") ||
                (navMode === "city-from" && activeMenu === "finish")) && (
                <>
                  <Button
                    className="mb-4 mt-2 flex h-auto gap-2 whitespace-normal bg-gray-100 text-foreground/70 hover:bg-gray-200 hover:text-foreground"
                    onClick={getLocation}
                  >
                    <FaLocationCrosshairs className="block text-base text-primary" />
                    {t("current-position")}
                  </Button>
                  {error && (
                    <p className="mt-2 text-xs text-rose-600">{error}</p>
                  )}
                </>
              )}
            <OverlayScrollbarsComponent
              className={cn(
                "max-h-[11rem] overflow-auto group-data-[dir=ltr]:pr-7 group-data-[dir=rtl]:pl-7",
                ((navMode === "city-to" && activeMenu === "finish") ||
                  (navMode === "city-from" && activeMenu === "start")) &&
                  "max-h-[15rem]",
              )}
              options={{
                scrollbars: { autoHide: "move", autoHideDelay: 3000 },
                overflow: { x: "hidden" },
              }}
              defer
            >
              <div>
                {loading ||
                  (query.length !== debouncedQuery.length && (
                    <p className="text-center">{t("loading")}</p>
                  ))}
                {!loading &&
                  results.length === 0 &&
                  debouncedQuery.length > 0 &&
                  query.length === debouncedQuery.length && (
                    <p className="text-center">{t("no-results")}</p>
                  )}
                {!loading &&
                  results.length > 0 &&
                  query.length === debouncedQuery.length && (
                    <ul className="flex flex-col gap-2">
                      <li className="font-semibold text-muted-foreground/80">
                        {t("suggestions")}
                      </li>
                      {results.map((result) => (
                        <li
                          key={result.place_id}
                          value={result.place_id}
                          className="flex cursor-pointer hover:text-primary"
                          onClick={() => onSelectHandler(result)}
                        >
                          <span className="flex-1">
                            {result.name ? result.name : result.display_name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                {((navMode === "city-to" && activeMenu === "finish") ||
                  (navMode === "city-from" && activeMenu === "start")) && (
                  <div>
                    {foundInTitle.length > 0 && (
                      <ul className="flex flex-col gap-2">
                        <li className="font-semibold text-muted-foreground/80">
                          {t("suggestions")}
                        </li>
                        {foundInTitle.map((poi) => (
                          <li
                            key={poi.id}
                            value={poi.id}
                            className="flex cursor-pointer lg:hover:text-primary"
                            onClick={() => onSelectHandler(poi)}
                          >
                            <span className="flex-1">
                              {poi.properties?.title}
                            </span>
                            <span className="font-bold">{poi.floorName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {foundInDescription.length > 0 && (
                      <ul className="flex flex-col gap-2">
                        <li className="mt-2 font-semibold text-muted-foreground/80">
                          {t("found-in-description")}
                        </li>
                        {foundInDescription.map((poi) => (
                          <li
                            key={poi.id}
                            value={poi.id}
                            className="flex cursor-pointer lg:hover:text-primary"
                            onClick={() => onSelectHandler(poi)}
                          >
                            <span className="flex-1">
                              {poi.properties?.title}
                            </span>
                            <span className="font-bold">{poi.floorName}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {foundInDescription.length === 0 &&
                      foundInTitle.length === 0 && (
                        <p className="py-4 text-center text-sm">
                          {t("no-results")}
                        </p>
                      )}
                  </div>
                )}
              </div>
            </OverlayScrollbarsComponent>
          </div>
        </div>
      </motion.div>
    </>
  );
}

export default CityRouteForm;
